import Card from '../../common/card';
import OnboardingDialog from '../../common/onboarding-dialog';
import { ReactComponent as UserIcon } from '../../../assets/icons/user-circle.svg';
import { Button } from '@mui/material';
import { Box, Paper, Typography, Alert, Checkbox, FormControlLabel, Divider, Container } from '@mui/material';
import { Info } from '@mui/icons-material';
import { useSplitPayments } from '../../../store/split-payments';
import { useHistory, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Routes } from '../../../config/routes';
import ButtonCustom from '../../common/__deprecated__/button-custom';
import { SplitPayment, SplitPaymentsActionType } from '../../../store/split-payments/interfaces';

const Resume = () => {
  const history = useHistory();
  const { collaboration_id: collaborationId } = useParams<{ collaboration_id: string }>();
  const [selectedSplit, setSelectedSplit] = useState<SplitPayment | null>(null);
  const { loading, performCollaborationAction, requestSuccess, allSplitPayments } = useSplitPayments();
  const [infoAgree, setInfoAgree] = useState(false);
  const [collaborationAgree, setCollaborationAgree] = useState(false);
  const [errors, setErrors] = useState<{ infoAgree?: string; collaborationAgree?: string }>({});

  useEffect(() => {
    const split = allSplitPayments?.data.find(split => split.id == Number(collaborationId));
    if (split) {
      setSelectedSplit(split);
    } else {
      history.push(Routes.SPLIT_PAYMENTS);
    }
  }, []);

  useEffect(() => {
    if (requestSuccess) {
      history.push(Routes.SPLIT_PAYMENTS);
    }
  }, [requestSuccess]);

  const handleRejectCollaboration = () => {
    performCollaborationAction(selectedSplit?.id || 0, SplitPaymentsActionType.REJECT);
  };

  const handleSubmit = () => {
    if (!infoAgree) {
      setErrors(prev => ({ ...prev, infoAgree: 'Debe aceptar la información.' }));
      return;
    }

    if (!collaborationAgree) {
      setErrors(prev => ({ ...prev, collaborationAgree: 'Debe aceptar los términos de colaboración.' }));
      return;
    }

    performCollaborationAction(selectedSplit?.id || 0, SplitPaymentsActionType.ACCEPT);
  };

  return (
    <OnboardingDialog
      dividers={false}
      unpaddingContent
      unpaddingFooter
      fullScreenMobile
      type="md"
      bgColor="#F9FAFB"
      title={`Split Payment - Invitar a Colaborador`}
      body={
        <>
          <div style={{ marginTop: '6em', marginLeft: '1em', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
            <div style={{ gridColumn: '2 / 5' }}>
              {selectedSplit && (
                <Card>
                  <div className="transaction_detail__title_section">
                    <UserIcon className="transaction_detail__title_section__icon" />
                    {'Resumen del acuerdo de Split Payment/Colaboración'}
                  </div>
                  <Container maxWidth="lg">
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        mb: 3,
                        color: 'text.secondary',
                        fontWeight: 400,
                      }}
                    >
                      La fecha de inicio del acuerdo se contará a partir del momento en que sea aceptado.
                    </Typography>

                    {/* Info Alert */}
                    <Alert
                      severity="info"
                      icon={<Info />}
                      sx={{
                        mb: 3,
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        border: '1px solid rgba(25, 118, 210, 0.12)',
                        '& .MuiAlert-icon': {
                          color: 'info.main',
                        },
                      }}
                    >
                      La fecha de inicio del acuerdo se contará a partir del momento en que sea aceptado.
                    </Alert>

                    {/* Main Form Paper */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 4,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                      }}
                    >
                      {/* Form Fields */}
                      <Box sx={{ mb: 4 }}>
                        {/* Name Field */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            Nombre
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            {selectedSplit.name}
                          </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        {/* Category Field */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            Categoría del colaborador/a
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            {selectedSplit.category}
                          </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        {/* Commission Field */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            Recibir comisiones de las ventas
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            Sí
                          </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        {/* Percentage Field */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            Porcentaje de las comisiones (%)
                          </Typography>
                          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            {selectedSplit.collaboratorCommission.toFixed(2)}%
                          </Typography>
                        </Box>
                      </Box>

                      {/* Checkboxes */}
                      <Box sx={{ mt: 4 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={infoAgree}
                              onChange={() => setInfoAgree(!infoAgree)}
                              sx={{
                                color: errors.infoAgree ? 'red' : 'black',
                                alignSelf: 'flex-start',
                                mt: -1,
                                '& .MuiSvgIcon-root': {
                                  fontSize: 20,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{ color: errors.infoAgree ? 'red' : 'text.secondary', lineHeight: 1.5 }}
                            >
                              Acepto la propuesta de colaboración.
                            </Typography>
                          }
                          sx={{
                            alignItems: 'flex-start',
                            mb: 2,
                            ml: 0,
                            '& .MuiFormControlLabel-label': {
                              ml: 1,
                            },
                          }}
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={collaborationAgree}
                              onChange={() => setCollaborationAgree(!collaborationAgree)}
                              sx={{
                                color: errors.collaborationAgree ? 'red' : 'black',
                                alignSelf: 'flex-start',
                                mt: -1,
                                '& .MuiSvgIcon-root': {
                                  fontSize: 20,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{ color: errors.collaborationAgree ? 'red' : 'text.secondary', lineHeight: 1.5 }}
                            >
                              Entiendo que el vendedor será responsable de gestionar reembolsos y contracargos, mientras que
                              el colaborador se encargará de la integración.
                            </Typography>
                          }
                          sx={{
                            alignItems: 'flex-start',
                            ml: 0,
                            '& .MuiFormControlLabel-label': {
                              ml: 1,
                            },
                          }}
                        />
                      </Box>
                    </Paper>
                  </Container>

                  <div className="split-payments__user-type__buttons">
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleRejectCollaboration}
                      sx={{ backgroundColor: '#E8E8E8', color: 'black', ':hover': { backgroundColor: '#D0D0D0' } }}
                    >
                      Rechazar colaboración
                    </Button>
                    <ButtonCustom
                      variant="contained"
                      size="large"
                      text={'ACEPTAR COLABORACIÓN'}
                      onClick={handleSubmit}
                      loading={loading}
                    />
                  </div>
                </Card>
              )}
            </div>
          </div>
        </>
      }
      menuItems={[
        { title: 'Introducción', completed: true, selected: false },
        {
          title: 'Tipo de usuario',
          subItems: [
            {
              title: 'Vendedor / Colaborador',
              completed: false,
              selected: true,
            },
          ],
        },
        {
          title: 'Información de colaboración',
          subItems: [
            {
              title: 'Información de colaboración',
              completed: true,
              selected: false,
            },
          ],
        },
        {
          title: 'Términos y condiciones',
          subItems: [
            {
              title: 'Aprobación de términos y condiciones',
              completed: true,
              selected: false,
            },
            {
              title: 'Resumen del acuerdo',
              completed: true,
              selected: false,
            },
          ],
        },
      ]}
    />
  );
};

export default Resume;
